import {HEX_DIRECTIONS} from "../../constants/geometry";
import {coordsToKey} from "../coords/coordsToKey";
import {keyToCoords} from "../coords/keyToCoords";
import {TERRAIN_TOKENS} from "../../constants/tokens";
import {REGISTRY_SINGLETON} from "../../scenes/Registry";
import {selectGridConfigState} from "../../redux/reducers/sharedStateReducer/selectors";

/**
 * Возвращает hexGridRenderer для расчетов бота: реальный рендерер, если он доступен,
 * иначе headless-объект {hexes} по радиусу поля (тот же прием, что в executePhaseAutoActions).
 */
// Headless-сетки по радиусу поля: сетка зависит только от радиуса, а зовут ее
// за шаг бота десятки раз. Никто из расчетов бота ее не меняет — только читает
const headlessGridRenderersByRadius = new Map();

export const getBotGridRenderer = (state) => {
  const renderer = REGISTRY_SINGLETON.getHexGridRenderer && REGISTRY_SINGLETON.getHexGridRenderer();
  if (renderer && renderer.hexes) {
    return renderer;
  }

  const gridConfig = selectGridConfigState(state);
  const {radius} = gridConfig;
  // Запоминаем только числовой радиус: для него сетка — функция значения
  const isCacheableRadius = typeof radius === 'number';
  const cachedRenderer = isCacheableRadius ? headlessGridRenderersByRadius.get(radius) : null;
  if (cachedRenderer) {
    return cachedRenderer;
  }

  const hexes = {};
  for (let q = -radius; q <= radius; q++) {
    for (let r = -radius; r <= radius; r++) {
      const s = -q - r;
      if (Math.abs(s) <= radius) {
        hexes[`${q},${r}`] = true;
      }
    }
  }

  const headlessRenderer = {hexes};
  if (isCacheableRadius) {
    headlessGridRenderersByRadius.set(radius, headlessRenderer);
  }
  return headlessRenderer;
};

// Ключи шести возможных соседей по ключу ячейки. Это чистая функция ключа, а
// разбор строки и сборка шести ключей — самое дорогое в поиске соседей, на
// котором стоят все BFS бота. Есть ли сосед на поле, проверяется каждый раз,
// поэтому смена поля запомненное не портит. Ключей на поле — сотни; предел —
// страховка на случай мусорных ключей
const NEIGHBOR_CANDIDATES_CACHE_LIMIT = 20000;
const neighborCandidatesByHexKey = new Map();

const getNeighborCandidateKeys = (hexKey) => {
  // Запоминаются только строковые ключи: для них соседи — функция значения
  if (typeof hexKey !== 'string') {
    const [q, r] = keyToCoords(hexKey);
    return HEX_DIRECTIONS.map(([dq, dr]) => coordsToKey(q + dq, r + dr));
  }
  let candidates = neighborCandidatesByHexKey.get(hexKey);
  if (!candidates) {
    const [q, r] = keyToCoords(hexKey);
    candidates = HEX_DIRECTIONS.map(([dq, dr]) => coordsToKey(q + dq, r + dr));
    if (neighborCandidatesByHexKey.size >= NEIGHBOR_CANDIDATES_CACHE_LIMIT) {
      neighborCandidatesByHexKey.clear();
    }
    neighborCandidatesByHexKey.set(hexKey, candidates);
  }
  return candidates;
};

/**
 * Соседние ячейки (существующие на поле)
 */
export const getNeighborHexKeys = (hexKey, gridRenderer) => {
  const candidates = getNeighborCandidateKeys(hexKey);
  const neighbors = [];
  for (let index = 0; index < candidates.length; index++) {
    if (gridRenderer.hexes[candidates[index]]) {
      neighbors.push(candidates[index]);
    }
  }
  return neighbors;
};

/**
 * Ячейки в радиусе снабжения от ферм: путь снабжения идет по НЕПУСТЫМ ячейкам
 * (сам отряд может стоять на пустой — последняя ячейка пути может быть пустой).
 * Приближение без учета ЗК противника. Возвращает Set<hexKey>.
 */
export const calcSupplyReachCells = (farmHexKeys, hexes, gridRenderer, maxDepth = 5) => {
  const reachable = new Set(farmHexKeys.filter((hexKey) => gridRenderer.hexes[hexKey]));
  let frontier = [...reachable];

  let depth = 0;
  while (frontier.length > 0 && depth < maxDepth) {
    depth += 1;
    const nextFrontier = [];
    for (const hexKey of frontier) {
      for (const neighborKey of getNeighborHexKeys(hexKey, gridRenderer)) {
        if (reachable.has(neighborKey)) {
          continue;
        }
        const token = hexes[neighborKey];
        if (token === TERRAIN_TOKENS.MOUNTAIN) {
          continue;
        }
        reachable.add(neighborKey);
        // Продолжаем путь только через непустые ячейки
        if (token) {
          nextFrontier.push(neighborKey);
        }
      }
    }
    frontier = nextFrontier;
  }

  return reachable;
};

/**
 * Волна 33: то же, что calcSupplyReachCells для одной фермы, но с ретрансляцией —
 * по правилам путь снабжения, дойдя до ДРУГОЙ своей фермы, начинает отсчет
 * дальности заново (calculateAvailableSupplyCells), поэтому ёмкость тыловой
 * фермы доходит до отрядов у фронта по цепочке ферм. Без учета блокировок.
 * ownFarmHexKeys — Set всех своих ферм (включая саму стартовую).
 */
export const calcSupplyReachCellsWithRelay = (farmHexKey, ownFarmHexKeys, hexes, gridRenderer, maxDepth = 5) => {
  const reachable = new Set();
  if (!gridRenderer.hexes[farmHexKey]) {
    return reachable;
  }
  reachable.add(farmHexKey);
  // Лучшая (наибольшая) оставшаяся дальность, с которой ячейка уже пройдена:
  // повторный заход имеет смысл только с большим запасом
  const bestRemaining = new Map([[farmHexKey, maxDepth]]);
  let frontier = [{hexKey: farmHexKey, remaining: maxDepth}];

  while (frontier.length > 0) {
    const nextFrontier = [];
    for (const {hexKey, remaining} of frontier) {
      if (remaining <= 0) {
        continue;
      }
      for (const neighborKey of getNeighborHexKeys(hexKey, gridRenderer)) {
        const token = hexes[neighborKey];
        if (token === TERRAIN_TOKENS.MOUNTAIN) {
          continue;
        }
        reachable.add(neighborKey);
        if (!token) {
          // Пустая ячейка — только конечная точка пути
          continue;
        }
        const nextRemaining = ownFarmHexKeys.has(neighborKey) ? maxDepth : remaining - 1;
        if ((bestRemaining.get(neighborKey) ?? -1) >= nextRemaining) {
          continue;
        }
        bestRemaining.set(neighborKey, nextRemaining);
        nextFrontier.push({hexKey: neighborKey, remaining: nextRemaining});
      }
    }
    frontier = nextFrontier;
  }

  return reachable;
};

/**
 * BFS-расстояния от набора стартовых ячеек по проходимым ячейкам поля (горы непроходимы).
 * Возвращает Map<hexKey, distance>.
 */
export const calcBfsDistances = (startHexKeys, hexes, gridRenderer, maxDepth = Infinity) => {
  const distances = new Map();
  let frontier = [];

  for (const startKey of startHexKeys) {
    if (!distances.has(startKey)) {
      distances.set(startKey, 0);
      frontier.push(startKey);
    }
  }

  let depth = 0;
  while (frontier.length > 0 && depth < maxDepth) {
    depth += 1;
    const nextFrontier = [];
    for (const hexKey of frontier) {
      // То же, что обход getNeighborHexKeys, но без промежуточного массива:
      // этот цикл — самый горячий у бота
      const candidates = getNeighborCandidateKeys(hexKey);
      for (let index = 0; index < candidates.length; index++) {
        const neighborKey = candidates[index];
        if (!gridRenderer.hexes[neighborKey] || distances.has(neighborKey)) {
          continue;
        }
        if (hexes[neighborKey] === TERRAIN_TOKENS.MOUNTAIN) {
          continue;
        }
        distances.set(neighborKey, depth);
        nextFrontier.push(neighborKey);
      }
    }
    frontier = nextFrontier;
  }

  return distances;
};
